/**
 * IfcPreviewDialog (WHA-3685) — the preview modal shown for `.ifc` (BIM) files.
 *
 * Replaces the old text-only confirm (WHA-3604) with a still screenshot of the
 * 3D structure: the pipeline pre-renders one canonical PNG per IFC at the
 * /models viewer's iso angle (cloud_metadata.thumbnail_path) and we fetch it by
 * document id via `getIfcThumbnailUrl`, so every surface — chatbot DocChips,
 * the building-data sidebar, and the /documents + /projects lists — shows the
 * exact same image. The "Open 3D model" action still deep-links to the full
 * viewer; "Close" dismisses.
 *
 * Best-effort image: while it loads we show a spinner, and if the document has
 * no thumbnail yet (old / not-yet-reprocessed IFC) or the image fails to load
 * we fall back to an icon + label. The model is always still openable in 3D
 * (when it has a building), so there's no regression for un-thumbnailed IFCs.
 */

import { useEffect, useState } from "react";
import { Box, Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { getIfcThumbnailUrl } from "@/stores/ifcModel/repository";
import { useIfcModelOverlay } from "@/domains/buildings/components/ifc-viewer/IfcModelOverlay";
import { useModelViewerGuard } from "@/hooks/usePermissionDenied";
import type { Document } from "@/types/documents";

interface IfcPreviewDialogProps {
  document: Document;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPreviewLoad?: () => void;
}

type PreviewStatus = "loading" | "image" | "fallback";

function useIfcThumbnail(documentId: Document["id"], open: boolean) {
  const [status, setStatus] = useState<PreviewStatus>("loading");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Fetch the canonical thumbnail whenever the dialog opens for a document.
  // Keyed on the id so reopening for a different IFC re-fetches; a cancelled
  // flag guards against a late resolve writing stale state after close/switch.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus("loading");
    setImageUrl(null);

    getIfcThumbnailUrl(Number(documentId))
      .then((url) => {
        if (cancelled) return;
        if (url) {
          setImageUrl(url);
          setStatus("image");
        } else {
          setStatus("fallback");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("fallback");
      });

    return () => {
      cancelled = true;
    };
  }, [documentId, open]);

  return {
    imageUrl,
    status,
    showFallback: () => setStatus("fallback"),
  };
}

interface IfcThumbnailPreviewProps {
  document: Document;
  imageUrl: string | null;
  status: PreviewStatus;
  onError: () => void;
  onLoad?: () => void;
}

function IfcThumbnailPreview({
  document,
  imageUrl,
  status,
  onError,
  onLoad,
}: IfcThumbnailPreviewProps) {
  const { t } = useLanguage();

  return (
    <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded-md bg-slate-900">
      {status === "loading" && (
        <div className="flex flex-col items-center gap-2 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="text-sm">{t.buildings.ifcViewer.previewLoading}</span>
        </div>
      )}
      {status === "image" && imageUrl && (
        <img
          src={imageUrl}
          alt={document.filename}
          className="h-full w-full object-contain"
          onLoad={onLoad}
          onError={onError}
        />
      )}
      {status === "fallback" && (
        <div className="flex flex-col items-center gap-2 text-slate-400">
          <Box className="h-10 w-10" strokeWidth={1.5} />
          <span className="text-sm">{t.buildings.ifcViewer.previewUnavailable}</span>
        </div>
      )}
    </div>
  );
}

interface IfcPreviewContentProps extends IfcThumbnailPreviewProps {
  onOpenChange: (open: boolean) => void;
  onOpenModel: () => void;
}

function IfcPreviewContent(props: IfcPreviewContentProps) {
  const { document, onOpenChange, onOpenModel } = props;
  const { t } = useLanguage();
  const buildingId = document.building_id;

  return (
    <DialogContent className="w-full max-w-2xl" onOpenAutoFocus={(event) => event.preventDefault()}>
      <DialogHeader>
        <DialogTitle className="truncate pr-6 text-base font-medium">
          {document.filename}
        </DialogTitle>
      </DialogHeader>

      <IfcThumbnailPreview {...props} />

      {!buildingId && (
        <p className="text-sm text-muted-foreground">{t.buildings.ifcViewer.noBuildingForModel}</p>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          {t.common.close}
        </Button>
        <Button onClick={onOpenModel} disabled={!buildingId}>
          {t.buildings.ifcViewer.open3dModel}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function IfcPreviewDialog({
  document,
  open,
  onOpenChange,
  onPreviewLoad,
}: IfcPreviewDialogProps) {
  const { openModel } = useIfcModelOverlay();
  const guardModelViewer = useModelViewerGuard();
  const { imageUrl, status, showFallback } = useIfcThumbnail(document.id, open);
  const buildingId = document.building_id;

  const handleOpenModel = () => {
    if (!buildingId) return;
    // WHA-3710: open the viewer as a fullscreen overlay in place (no route
    // change) so closing it returns the user to wherever they were previewing.
    guardModelViewer(document.project_id, () => {
      openModel(Number(buildingId), Number(document.id));
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <IfcPreviewContent
        document={document}
        imageUrl={imageUrl}
        status={status}
        onError={showFallback}
        onLoad={onPreviewLoad}
        onOpenChange={onOpenChange}
        onOpenModel={handleOpenModel}
      />
    </Dialog>
  );
}
